import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/work", label: "Work" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  const isHome = location.pathname === "/";
  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out ${
          scrolled
            ? "bg-white/95 backdrop-blur-sm shadow-[0_1px_0_rgba(0,0,0,0.04)]"
            : isHome
            ? "bg-transparent"
            : "bg-white"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-center justify-between h-20">
            <Link
              to="/"
              className="nav-logo text-[15px] tracking-[0.15em] uppercase"
              style={{
                color: scrolled || !isHome ? "#1a1a1a" : "#ffffff",
              }}
            >
              Jayant Krishna Rohatgi
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="nav-link relative text-[13px] tracking-[0.12em] uppercase transition-opacity duration-500"
                  style={{
                    color: scrolled || !isHome ? "#1a1a1a" : "#ffffff",
                    opacity: isActive(link.to) ? 1 : 0.6,
                  }}
                >
                  {link.label}
                  {isActive(link.to) && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-1 left-0 right-0 h-[1px]"
                      style={{
                        backgroundColor:
                          scrolled || !isHome ? "#1a1a1a" : "#ffffff",
                      }}
                      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                    />
                  )}
                </Link>
              ))}
            </div>

            {/* Mobile Toggle */}
            <button
              className="md:hidden p-2 -mr-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <X
                  size={20}
                  color={scrolled || !isHome ? "#1a1a1a" : "#ffffff"}
                />
              ) : (
                <Menu
                  size={20}
                  color={scrolled || !isHome ? "#1a1a1a" : "#ffffff"}
                />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-white flex flex-col items-center justify-center"
          >
            <div className="flex flex-col items-center gap-8">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08, duration: 0.4 }}
                >
                  <Link
                    to={link.to}
                    className={`text-[15px] tracking-[0.15em] uppercase transition-opacity duration-300 ${
                      isActive(link.to)
                        ? "text-[#1a1a1a] opacity-100"
                        : "text-[#1a1a1a] opacity-50"
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
